import { Injectable } from '@angular/core';
import { CompletionRequest, Conversation, CompletionMessage, StreamChunkData } from '@app-libs/model';
import { Observable, map } from 'rxjs';
import { TokenUsage } from './chat-api-service.interface';
import { ChatStateService } from './chat-state.service';
// Note: StreamManager import removed - now using unified API
import { CompletionApiService } from '../api/completion-api.service';
import { AssistantContext } from './assistant/assistant-context.service';
import { AssistantThread } from './assistant/assistant-thread.service';
import { ConversationManager } from './conversation/conversation-manager.service';
import { ConversationTitleService } from './conversation/conversation-title.service';
import { MessageManager } from './message/message-manager.service';
import { AudioTranscriptionService } from './utils/audio-transcription.service';
// Note: ChatInitializationService import removed - now used directly in ChatOrchestrator
import { MessageTransformer } from '../../common/utils/message-transformer.utils';

/**
 * Refactored ChatApiService using new state management API
 *
 * Key changes:
 * - Uses getter methods instead of direct state access
 * - Uses updateState() for state modifications
 * - Reactive observable subscriptions where possible
 * - No direct state mutations
 */
@Injectable({
  providedIn: 'root',
})
export class ChatApiService {
  constructor(
    private stateService: ChatStateService,
    // Note: streamManager removed - now using unified API
    private completionService: CompletionApiService,
    private conversationManager: ConversationManager,
    private conversationTitleService: ConversationTitleService,
    private messageManager: MessageManager,
    private assistantContext: AssistantContext,
    private assistantThread: AssistantThread,
    private audioTranscriptionService: AudioTranscriptionService,
    // Note: chatInitializationService removed - now injected in ChatOrchestrator
  ) { }

  // ===== AI Provider Operations =====

  // Note: streamChatCompletion() removed - replaced by createUnifiedCompletionStream()

  /**
   * Unified completion streaming method - Phase 4 implementation
   * 
   * This method consolidates ALL frontend conditional logic by delegating to the backend.
   * It replaces the complex 3-path logic in ChatOrchestrator.getStreamingResponse():
   * - Assistant detection → Backend ChatTypeDetectionService
   * - Parameter transformation → Backend ParameterTransformationService  
   * - Feature flag logic → Backend FeatureFlagService
   * 
   * Single source of truth, ~70% complexity reduction.
   */
  createUnifiedCompletionStream(
    messages: Partial<CompletionMessage>[],
    model: string,
    options?: {
      temperature?: number;
      maxTokens?: number;
      signal?: AbortSignal;
      currentUserMessage?: Partial<CompletionMessage>;
      // Assistant-specific fields (auto-detected by backend)
      threadId?: string;
      assistantId?: string;
      instruction?: string;
      // Additional completion fields
      topP?: number;
      frequencyPenalty?: number;
      presencePenalty?: number;
      stop?: string[];
      seed?: number;
      responseFormat?: { type: 'text' | 'json_object' };
    }
  ): Observable<StreamChunkData> {
    // Build unified completion request with all necessary context
    // Start with basic request from MessageTransformer
    const baseRequest = MessageTransformer.createCompletionRequest(
      messages,
      model,
      this.stateService.workspaceId,
      {
        stream: true,
        temperature: options?.temperature,
        maxTokens: options?.maxTokens,
        topP: options?.topP,
        frequencyPenalty: options?.frequencyPenalty,
        presencePenalty: options?.presencePenalty,
        stop: options?.stop,
        seed: options?.seed,
        responseFormat: options?.responseFormat
      }
    );

    // Add additional fields for unified backend logic
    const completionRequest: CompletionRequest = {
      ...baseRequest,
      currentUserMessage: options?.currentUserMessage,
      // Add context for backend chat type detection
      threadId: this.stateService.threadId,
      // Don't set assistantId directly - let backend extract from assistant object
      instruction: options?.instruction || (this.stateService.assistant as any)?.system_instructions,
      // Add full assistant object for backend reference
      assistant: this.stateService.assistant && this.stateService.assistant.assistant_id ? {
        assistant_id: this.stateService.assistant.assistant_id,
        external_id: this.stateService.assistant.external_id || null,
        system_instructions: (this.stateService.assistant as any)?.system_instructions,
        settings: (this.stateService.assistant as any)?.settings || null
      } : null,
      // Add conversation context for thread detection
      conversation: this.stateService.conversation ? {
        threadId: (this.stateService.conversation as any).threadId,
        conversationId: (this.stateService.conversation as any).conversationId
      } : null,
      signal: options?.signal
      // Note: Tool availability now determined by assistant.settings.available_tools
    };

    console.debug('Creating unified completion stream with request:', completionRequest);

    // Single API call to unified backend endpoint
    // Backend handles all conditional logic that was previously in frontend
    return this.completionService.createCompletion(completionRequest);
  }

  // ===== Conversation Management =====

  createConversation(initialContent: string): Observable<Conversation> {
    // Delegate to ConversationManager with exact same interface
    return this.conversationManager.createConversation({
      initialMessage: initialContent,
      workspaceId: this.stateService.workspaceId,
      assistantId: this.stateService.assistant.assistant_id,
    });
  }

  updateConversation(conversationId: string, updates: Partial<Conversation>): Observable<void> {
    // Delegate to ConversationManager with exact same interface
    return this.conversationManager
      .updateConversation(conversationId, updates)
      .pipe(map(() => void 0));
  }

  generateConversationTitle(): Observable<string | null> {
    // Delegate to ConversationTitleService with exact same interface
    return this.conversationTitleService.generateTitle(this.stateService.messages as CompletionMessage[]);
  }

  // ===== Message Operations =====

  createUserMessage(message: Partial<CompletionMessage>): Observable<CompletionMessage> {
    // Delegate to MessageManager with exact same interface
    return this.messageManager.createUserMessage(message);
  }

  createAssistantMessage(usage?: TokenUsage, toolCalls?: any[]): Observable<CompletionMessage> {
    // Delegate to MessageManager with exact same interface
    return this.messageManager.createAssistantMessage(usage, toolCalls);
  }

  updateMessage(messageId: string, updates: Partial<CompletionMessage>): Observable<void> {
    // Delegate to MessageManager with exact same interface
    return this.messageManager.updateMessage(messageId, updates).pipe(map(() => void 0));
  }

  /**
   * Creates or retrieves a thread for assistant conversations
   * Now delegates to AssistantThread service
   */
  createAssistantThread(): Observable<string> {
    return this.assistantThread.createAssistantThread();
  }

  /**
   * Transcribes audio to text using OpenAI Whisper
   * (Delegates to AudioTranscriptionService)
   */
  transcribeAudio(audioBlob: Blob): Observable<string> {
    return this.audioTranscriptionService.transcribeAudio(audioBlob);
  }

  /**
   * Uploads a file to OpenAI for use in completions
   * (Now delegates to unified CompletionApiService)
   */
  uploadFileToOpenAI(file: File): Observable<{ fileId: string; correlationId: string }> {
    return this.completionService.uploadFile(file, 'openai');
  }

  // ===== Chat Initialization Operations =====

  // Note: initChat() removed - ChatOrchestrator now calls ChatInitializationService directly

  /**
   * Checks if the current chat is an external assistant chat.
   */
  isAssistantChatExternal(): boolean {
    // Delegate to AssistantContext with exact same interface
    return this.assistantContext.isAssistantChatExternal();
  }
}
